import { ColorEnum } from '@/constants/color'
import { getActivityActorLabel } from '@/features/orders/components/OrderDetail/Footer/OrderHistoryCollapsible/utils/getActivityActorLabel'
import { getActivityPresentation } from '@/features/orders/components/OrderDetail/Footer/OrderHistoryCollapsible/utils/getActivityPresentation'
import { OrderActivityLog } from '@/features/orders/types/app'
import Collapsible from '@/features/ui/components/Collapsible'
import ChevronDownIcon from '@/features/ui/icons/components/ChevronDownIcon'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import dayjs from 'dayjs'
import { FC, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, StyleSheet, Text, View } from 'react-native'
import Animated, {
    Easing,
    useAnimatedStyle,
    useDerivedValue,
    withTiming,
} from 'react-native-reanimated'

interface OrderHistoryCollapsibleProps {
    activityLogs?: OrderActivityLog[]
}

const OrderHistoryCollapsible: FC<OrderHistoryCollapsibleProps> = ({ activityLogs }) => {
    const [expanded, setExpanded] = useState(false)
    const { t } = useTranslation()
    const screenLayout = useScreenLayout()

    const reversedActivityLogs = useMemo(() => activityLogs?.toReversed() ?? [], [activityLogs])

    const rotation = useDerivedValue(() =>
        withTiming(expanded ? 0 : 180, {
            duration: 220,
            easing: Easing.out(Easing.cubic),
        })
    )

    const chevronAnimatedStyle = useAnimatedStyle(() => ({
        transform: [{ rotate: `${rotation.value}deg` }],
    }))

    if (!reversedActivityLogs.length) return null

    return (
        <View style={styles.root}>
            <Collapsible
                open={expanded}
                containerStyle={styles.collapsibleContainer}
                contentStyle={styles.collapsibleContent}
            >
                <View style={styles.historyList}>
                    {reversedActivityLogs.map((activityLog, index) => {
                        const actorLabel = getActivityActorLabel(activityLog, t)
                        const activityPresentation = getActivityPresentation(activityLog, t)

                        return (
                            <Typography.Description
                                key={`${activityLog.occurredAt}-${index}`}
                                size="tiny"
                            >
                                <Text style={styles.dateText}>
                                    {dayjs(activityLog.occurredAt).format('D. MMM HH:mm')}
                                </Text>
                                {'  '}
                                {activityPresentation.parts.map((part, partIndex) => (
                                    <Typography.Description
                                        key={partIndex}
                                        font="medium"
                                        size="tiny"
                                        style={
                                            part.emphasis
                                                ? styles.emphasizedMessageText
                                                : styles.messageText
                                        }
                                    >
                                        {part.text}
                                    </Typography.Description>
                                ))}
                                {actorLabel && (
                                    <Text style={styles.actorText}>{` - ${actorLabel}`}</Text>
                                )}
                            </Typography.Description>
                        )
                    })}
                </View>
            </Collapsible>
            <Pressable
                accessibilityRole="button"
                onPress={() => setExpanded((prev) => !prev)}
                style={styles.header}
            >
                <Typography.Heading
                    variant={screenLayout === 'compact' ? 'h4' : 'h3'}
                    style={styles.title}
                >
                    {t('order.detail.historyTitle')}
                </Typography.Heading>
                <Animated.View style={chevronAnimatedStyle}>
                    <ChevronDownIcon color={ColorEnum.GRAY_900} />
                </Animated.View>
            </Pressable>
            <View style={styles.divider} />
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        alignSelf: 'stretch',
        width: '100%',
    },
    collapsibleContainer: {
        alignSelf: 'stretch',
    },
    collapsibleContent: {
        paddingTop: 8,
        paddingBottom: 16,
        paddingHorizontal: 0,
    },
    historyList: {
        gap: 8,
    },
    header: {
        alignSelf: 'stretch',
        paddingTop: 0,
        paddingBottom: 8,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    title: {
        flex: 1,
        color: ColorEnum.GRAY_900,
    },
    divider: {
        height: 1,
        backgroundColor: ColorEnum.GRAY_300,
    },
    dateText: {
        color: ColorEnum.GRAY_600,
    },
    actorText: {
        color: ColorEnum.GRAY_900,
    },
    messageText: {
        color: ColorEnum.GRAY_900,
    },
    emphasizedMessageText: {
        color: ColorEnum.EMERALD_700,
    },
})

export default memo(OrderHistoryCollapsible)
